import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {createQuoteTags, updateQuoteTags} from '../../actions/taggingDataActions';

import ReviewQuoteSelection from "./ReviewQuoteSelection";
import QuoteTags from "../quotes/QuoteTags";

import {QUOTE_TAG_TYPE_MAPPING, VENDOR_TAG_TYPE} from '../../constants/constants';

export default function ReviewTagging(props) {
    const dispatch = useDispatch();
    const [selectedText, setSelectedText] = useState("");
    const [isNewQuote, setIsNewQuote] = useState(true);
    const [tagsMapping, setTagsMapping] = useState([]);
    // tagsMapping: [{id: 0, text: '', tags: {}, isSelected: false}]

    const { tags, quotesAndTags, review } = useSelector(state => state.taggingData);

    const [newTags, setNewTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState({});
    const [renderQuotesLink, setRenderQuotesLink] = useState(props.hasQuotes);

    const [isSelectingText, setIsSelectingText] = useState(false);

    // Listener to changes in quotes and tags in the store, update local state accordingly 
    useEffect(() => {
        if (quotesAndTags && Object.keys(quotesAndTags).length) {
            if (tagsMapping.length != quotesAndTags.vendor_quotes.length && review.id == props.cacheReviewID) {
                // quote deleted
                onTextSelect("");
            }

            if (review.id != props.cacheReviewID || (tagsMapping.length != quotesAndTags.vendor_quotes.length)) {
                // set new tags mapping if review changed or quote deleted
                props.setCacheReviewID(review.id);
                let newTagsMapping = [];
                quotesAndTags.vendor_quotes.map((quote, index) => {
                    let tags = {};
                    quotesAndTags.quote_tags.map((quoteTag) => {
                        if (quoteTag.key_point_id == quote.id) {
                            if (!Object.keys(tags).includes(QUOTE_TAG_TYPE_MAPPING[quoteTag.tag_type])) {
                                tags[QUOTE_TAG_TYPE_MAPPING[quoteTag.tag_type]] = [quoteTag.tag];
                            } else {
                                tags[QUOTE_TAG_TYPE_MAPPING[quoteTag.tag_type]].push(quoteTag.tag);
                            }
                        }
                    });
                    let isSelected = selectedText == quote.body;
                    newTagsMapping.push({id: quote.id, text: quote.body, tags: tags, isSelected: isSelected});
                    if(isSelected) {
                        setIsSelectingText(true);
                        setSelectedTags(tags);
                        setSelectedText(quote.body);
                    }
                })
                setTagsMapping(newTagsMapping);
            }
        }
    }, [quotesAndTags]);

    // Once tags change set relevant quotes, for different quotes from different reviews selection
    useEffect(() => {
        if (props.selectedQuote && tagsMapping.length) {
            onQuoteSelect(props.selectedQuote);
            props.setSelectedQuote(null);
        }
    },[tagsMapping]);

    useEffect(() => {
        // for cases where the review data does not get updated
        if (props.selectedQuote && tagsMapping.length && props.review && props.cacheReviewID == props.review.id) {
            onQuoteSelect(props.selectedQuote);
            props.setSelectedQuote(null);
        }
    },[props.selectedQuote]);

    useEffect(() => {
        // prevent previously selected unsaved text from saving for a different review
        if(!props.taggingEnabled) {
            setIsSelectingText(false);
            setSelectedText('');
        }
    }, [props.taggingEnabled]);

    const setSelectingText = (isSelecting) => {
        setIsSelectingText(isSelecting);
        setIsNewQuote(true);
    }

    const onTextSelect = (selectedText) => {
        if (selectedText) {
            setIsSelectingText(true);
        } else {
            setIsSelectingText(false);
        }
        onQuoteSelect("");
        setSelectedText(selectedText);
    }

    // On tags save update local state and dispatch changes to server
    const onTagsSave = () => {
        if (selectedText) {
            let newTagsMapping = [...tagsMapping];

            newTagsMapping.map((mapping, index) => {
                if (mapping.text == selectedText) {
                    newTagsMapping.splice(index, 1);
                }
            })

            let translatedSelectedTags = {};
            Object.keys(selectedTags).map((tagType) => {
                const translatedTagType = Object.keys(QUOTE_TAG_TYPE_MAPPING).find((key) => QUOTE_TAG_TYPE_MAPPING[key] == tagType);
                translatedSelectedTags[translatedTagType] = selectedTags[tagType];
            }) 

            const quoteTag = quotesAndTags.vendor_quotes.find((vq) => (vq.body == selectedText));

            if (quoteTag && quoteTag.id) {
                newTagsMapping.push({id: quoteTag.id, text: selectedText, tags: selectedTags, isSelected: true});
                setTagsMapping(newTagsMapping);
                dispatch(updateQuoteTags(translatedSelectedTags, quoteTag.id));
            } else {
                dispatch(createQuoteTags(selectedText, translatedSelectedTags, props.review.id));
                if(!renderQuotesLink && props.quotesLink !== undefined) {
                    setRenderQuotesLink(true);
                }
            }
        }
    }

    // On quote select handler
    const onQuoteSelect = (quote) => {
        let newTags = [...tagsMapping];
        setSelectedTags({});
        newTags.map((tagObj) => {
            const isSelected = tagObj.text == quote;
            if (isSelected) {
                setIsSelectingText(true);
                setSelectedTags(tagObj.tags);
            }
            tagObj.isSelected = isSelected;
        })

        setIsNewQuote(false);
        setSelectedText(quote);
        setTagsMapping(newTags);
    }

    // On tags select update local state
    const onTagSelect = (tag, tagType) => {
        let newSelectedTags = {...selectedTags};
        if (!newSelectedTags[tagType]) {
            newSelectedTags[tagType] = [];
        }

        if (newSelectedTags[tagType].includes(tag)) {
            newSelectedTags[tagType].splice(newSelectedTags[tagType].indexOf(tag), 1);
        } else {
            newSelectedTags[tagType].push(tag);
        }

        setSelectedTags(newSelectedTags);
    }

    // On add new tag update local state
    const onAddNewTag = (tag) => {
        let newTagsArray = [...newTags];
        if (!newTagsArray.includes(tag)) {
            newTagsArray.push(tag);
        }
        setNewTags(newTagsArray);
        onTagSelect(tag, VENDOR_TAG_TYPE);
    }

    const taggingProcess = {
        1: {
            class: 'success', text: 'Highlight a quote or select an existing quote'
        },
        2: {
            class: 'success', text: 'Choose tags that describe your quote'
        },
        3: {
            class: 'success', text: 'Save your work'
        }
    }

    const renderTaggingProcess = () => {
        return (
            Object.keys(taggingProcess).map((key, index) => {
                return (
                    <div className="d-flex flex-row align-items-center me-4" key={index}>
                        <div className={`avatar-sm rounded-circle bg-soft-${taggingProcess[key].class} border-${taggingProcess[key].class} border text-center`}>
                            <span className={`font-22 avatar-title text-${taggingProcess[key].class}`}>{key}</span>
                        </div>
                        <span className={'font-15 text-secondary ms-2'}>{taggingProcess[key].text}</span>
                    </div>
                )
            })
        )
    }

    // Main component renderer
    return (
        <div className={'row'} style={{height: '100vh'}}>
            <div className={'col-xl-12 border-bottom'}>
                <div className="mx-4 mt-2 mb-2">
                    <div className='d-flex align-items-center mb-1'>
                        <h4 className={'page-title'}>Quote Tagging Widget</h4>
                    </div>

                    <div className="d-flex flex-row mb-2">
                        {renderTaggingProcess()}
                    </div>
                </div>
            </div>
            <div id="ReviewDisplayContent" className={'col-xl-12 d-flex flex-row'} style={{height: '85vh'}}>
                <ReviewQuoteSelection
                    review={review}
                    onTextSelect={onTextSelect}
                    tagsMapping={tagsMapping}
                    onQuoteSelect={onQuoteSelect}
                    selectedText={selectedText}
                    setSelectingText={setSelectingText}
                />
                <QuoteTags
                    selectedText={selectedText}
                    onTagsSave={onTagsSave}
                    onAddNewTag={onAddNewTag}
                    newTags={newTags}
                    tags={tags}
                    onTagSelect={onTagSelect}
                    selectedTags={selectedTags}
                    isSelectingText={isSelectingText}
                    onDeleteHandler={props.onDeleteHandler}
                    quotesLink={props.quotesLink}
                    renderQuotesLink={renderQuotesLink}
                    isNewQuote={isNewQuote}
                />
            </div>
        </div>
    )
}

